import { MusicIcon } from './icons/MusicIcon';
import { ReactNode, useState, useEffect } from 'react';
import SceneRouteMotion from './SceneRouteMotion';
import { useAnimatedFavicon } from '../hooks/useAnimatedFavicon';
import { useBrandMotion } from '../hooks/useBrandMotion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useSocketStore } from '../stores/socketStore';
import { LogOut, AlertTriangle, Pause, Play } from 'lucide-react';

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const brandMotion = useBrandMotion();
  useAnimatedFavicon(brandMotion.enabled);
  const location = useLocation();
  const navigate = useNavigate();
  const { player, isAuthenticated, logout } = useAuthStore();
  const { isConnected, currentRoom, leaveRoom, publicRooms, fetchPublicRooms } = useSocketStore();
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Charger les rooms publiques et le nombre de joueurs en ligne
  useEffect(() => {
    if (isConnected) {
      fetchPublicRooms();
      const interval = setInterval(fetchPublicRooms, 15000);
      return () => clearInterval(interval);
    }
  }, [isConnected, fetchPublicRooms]);
  
  // Vérifie si on est dans une room (page /room/* ou /game/*)
  const isInRoom = location.pathname.startsWith('/room/') || location.pathname.startsWith('/game/');
  
  // Handler pour les liens qui doivent vérifier si on est dans une room
  const handleNavClick = (e: React.MouseEvent, to: string) => {
    if (isInRoom && currentRoom) {
      e.preventDefault();
      setPendingNavigation(to);
      setShowLeaveModal(true);
    }
  };
  
  // Confirmer et quitter la room
  const handleConfirmLeave = () => {
    leaveRoom();
    setShowLeaveModal(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };
  
  // Annuler
  const handleCancelLeave = () => {
    setShowLeaveModal(false);
    setPendingNavigation(null);
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      <header className="scene-header">
        <div className="scene-nav">
          <div className="scene-brand"><Link to="/" className="scene-logo" onClick={(e) => { setMenuOpen(false); handleNavClick(e, '/'); }} aria-label="TheBlindTest, accueil"><span className="scene-logo-bars" data-paused={!brandMotion.enabled} aria-hidden="true"><i/><i/><i/><i/><i/></span>theblindtest</Link><button className="scene-meter-toggle" type="button" onClick={brandMotion.toggle} aria-label={brandMotion.enabled ? 'Mettre le vu-mètre en pause' : 'Animer le vu-mètre'} title={brandMotion.enabled ? 'Mettre le vu-mètre en pause' : 'Animer le vu-mètre'}>{brandMotion.enabled ? <Pause size={13}/> : <Play size={13}/>}</button></div>
          <nav className="scene-desktop-nav" aria-label="Navigation principale">
            <Link to="/" onClick={(e) => handleNavClick(e, '/')}>Jouer</Link>
            <Link to="/games" onClick={(e) => handleNavClick(e, '/games')}>Parties{publicRooms.length > 0 ? ` (${publicRooms.length})` : ''}</Link>
            <Link to="/leaderboard" onClick={(e) => handleNavClick(e, '/leaderboard')}>Classement</Link>
            <Link to="/premium" onClick={(e) => handleNavClick(e, '/premium')}>Premium</Link>
          </nav>
          <div className="scene-account">{isAuthenticated && player ? <><Link to="/profile" onClick={(e) => handleNavClick(e, '/profile')}>{player.name}</Link><button onClick={logout} aria-label="Déconnexion"><LogOut size={18}/></button></> : <Link to="/?auth=login" onClick={(e) => handleNavClick(e, '/?auth=login')}>Se connecter</Link>}</div>
          <button className="scene-menu-toggle" aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'} aria-expanded={menuOpen} aria-controls="scene-mobile-menu" onClick={() => setMenuOpen(!menuOpen)}><MusicIcon name={menuOpen ? 'close' : 'menu'} size={24}/></button>
        </div>
        {menuOpen && <nav id="scene-mobile-menu" className="scene-mobile-menu" aria-label="Navigation mobile">
          {[['/', 'Jouer'], ['/games', 'Parties'], ['/leaderboard', 'Classement'], ['/premium', 'Premium'], ...(isAuthenticated && player ? [['/profile', player.name]] : [['/?auth=login', 'Se connecter']])].map(([to, label]) => <Link key={label} to={to} onClick={(e) => { setMenuOpen(false); handleNavClick(e, to); }}>{label}</Link>)}
          {isAuthenticated && <button onClick={() => { setMenuOpen(false); logout(); }}>Déconnexion</button>}
        </nav>}
      </header>
      <main className="scene-main"><SceneRouteMotion>{children}</SceneRouteMotion></main>
      <footer className="scene-footer"><span>TheBlindTest, la musique se partage.</span><a href="https://bluehoodgames.com" target="_blank" rel="noopener noreferrer">Bluehood Games</a></footer>

      {/* Modal de confirmation pour quitter la room */}
      {showLeaveModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4">
          <div role="dialog" aria-modal="true" aria-labelledby="leave-title" className="bg-dark-800 rounded-2xl p-6 max-w-md w-full shadow-xl border border-dark-700">
            <div className="flex items-center gap-3 mb-4 text-warning">
              <AlertTriangle className="w-8 h-8" />
              <h2 id="leave-title" className="font-display text-xl font-bold">Quitter la partie ?</h2>
            </div>
            <p className="text-dark-300 mb-6">
              Vous êtes actuellement dans une room. Si vous quittez maintenant, vous perdrez votre progression.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleCancelLeave}
                className="flex-1 btn-secondary"
              >
                Rester
              </button>
              <button
                onClick={handleConfirmLeave}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold py-3 px-6 rounded-xl transition-all"
              >
                Quitter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

